
import { cn } from '../../../shared/lib'
import { Button } from '../../../shared/ui'
import { useState, useCallback, useRef, useEffect } from 'react'
import { ExportCurve, SearchNormal } from 'iconsax-reactjs';
import { useQuery } from '@tanstack/react-query';
import { adminActivityService, handleExportActivityLog } from '../service';
import Loader from 'src/legacy/components/loader';
import { ActivityLogType } from '../types';
import { paginationData } from 'src/legacy/libs/constants';
import dayjs from 'dayjs';
import { formatLocation, getDeviceName } from '../../../shared/lib/utils';
import FilterDropDown from '../../../shared/components/FilterDropDown';
import { useQueryState } from 'nuqs';
import PaginationV3 from '../../../../legacy/components/v2/pagination';
import { getModuleIcon } from '../../../features/activity/components/activityColumns';
import { useSelector } from 'react-redux';

const sortList = [
  { text: "Recent", value: "" },
  { text: "Past Week", value: "week" },
  { text: "Past Month", value: "month" },
  { text: "Past Year", value: "year" },
]

type SortValue = "" | "week" | "month" | "year";

const getDateRange = (value: SortValue): { startDate: string; endDate: string } | {} => {
  const endDate = dayjs().endOf("day").toISOString();

  const startDateMap: Record<Exclude<SortValue, "">, string> = {
    week: dayjs().subtract(1, "week").startOf("day").toISOString(),
    month: dayjs().subtract(1, "month").startOf("day").toISOString(),
    year: dayjs().subtract(1, "year").startOf("day").toISOString(),
  };

  if (!value) return {};

  return { startDate: startDateMap[value], endDate };
};


const ActivityLog = () => {
  const [sortTrx, setSortTrx] = useState("recent");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");
  const totalItemPerpage = useSelector((state: any) => state.totalItemPerpage)
  const [activityStatus, setActivityStatus] = useQueryState("status", {
    defaultValue: "",
  })
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { refetch, isLoading: downloadingCsv } = useQuery({
    queryFn: handleExportActivityLog,
    queryKey: ['export-activity-log'],
    enabled: false,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (totalItemPerpage) {
      setLimit(totalItemPerpage)
    }
  }, [totalItemPerpage])


  const debouncedSetSearch = useCallback((value: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearch(value);
    }, 500);
  }, []);

  const gotoPage = (requestedPage: number) => {
    return setCurrentPage(requestedPage);
  };
  const handleSwitchTab = (tab: string) => {
    setActivityStatus(tab);
    setCurrentPage(1)
  };

  const filterButtons = [
    { label: 'All', value: '' },
    { label: 'Success', value: 'Success' },
    { label: 'Failed', value: 'Failed' },
  ]

  const dateRange = getDateRange(sortTrx as SortValue);

  const { data: activityLog, isLoading } = useQuery({
    queryKey: ["activity-log", activityStatus, currentPage, search, sortTrx, limit],
    queryFn: () => adminActivityService({
      page: currentPage,
      q: search,
      limit: limit,
      status: activityStatus === '' ? undefined : activityStatus,
      ...dateRange,
    }),
  })

  const activityLogData = activityLog?.data?.map((item: ActivityLogType) => ({
    actions: item.action,
    module: item.module || "N/A",
    device: getDeviceName(item.userAgent),
    outcome: item.status,
    location: formatLocation(item.location),
    timestamp: dayjs(item.createdAt).format("MMM D, YYYY • h:mm A"),
  }))

  const activityMeta = activityLog?.meta;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        {filterButtons.map(({ label, value }) => (
          <Button
            key={label}
            className={cn(
              "rounded-[4px] h-8 hover:bg-none",
              activityStatus === value ? "bg-primary-10 !text-primary-20 font-[450]" : "bg-transparent text-gray-10 font-[350]"
            )}
            size="sm"
            variant="ghost"
            onClick={() => handleSwitchTab(value)}
          >
            {label}
          </Button>
        ))}
      </div>
      <main className="flex flex-col gap-4">
        <div className='flex gap-2 flex-wrap items-center justify-between w-full'>
          <div className='flex  flex-wrap items-center gap-5'>
            <div className="flex gap-1 w-[368px] h-10 items-center py-3 px-4 bg bg-gray-60 rounded-[8px]">
              <SearchNormal size={20} />
              <input
                type="text"
                placeholder="Search by business name"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  debouncedSetSearch(e.target.value);
                }}
                className="w-full border-none placeholder:text-xs outline-none focus:outline-0 focus-visible:outline-0 focus:border-0 bg-gray-60"
              />
            </div>
            <div className="border-l hidden md:block border-[#E7E9FB] h-5" />
            <FilterDropDown
              selected={sortTrx}
              allTime={false}
              onSelect={(value: string) => setSortTrx(value)}
              mainTitle="Sort"
              label="Recent"
              dropDownList={sortList}
            />
          </div>
          <Button
            disabled={isLoading || downloadingCsv}
            onClick={() => refetch()}
            className="rounded-[8px] h-10"
          >
            <ExportCurve size={20} />
            {downloadingCsv ? "Exporting..." : "Export"}
          </Button>
        </div>
        <div className="w-full overflow-x-auto">
          <div className="w-full border-collapse min-w-[800px]">
            <div className="">
            <div className="flex gap-3 w-full px-3 items-center !bg-primary-70 !rounded-t-[8px] h-8 !border-b !border-[#F7F8FD]">
              <div className="w-[30%] font-[500] text-xs text-gray-30">Action</div>
              <div className="w-[16%] font-[500] text-xs text-gray-30">Module</div>
              <div className="w-[14%] font-[500] text-xs text-gray-30">Device</div>
              <div className="w-[14%] font-[500] text-left text-xs text-gray-30">Outcome</div>
              <div className="w-[14%] font-[500] text-left text-xs text-gray-30">Location</div>
              <div className="w-[15%] font-[500] text-left text-xs text-gray-30">Timestamp</div>
            </div>
          </div>
            <div className="">
            {isLoading ? <Loader size={50} /> : activityLogData?.map((invoice: any, index: number) => (
              <div className="flex gap-3 px-3 border-b border-gray-60 h-[60px] w-full items-center" key={invoice.actions + index}>
                <div className="font-[450] text-sm text-gray-30 !w-[30%]">{invoice.actions}</div>
                <div className="font-[350] text-sm text-gray-30 !w-[16%] flex items-center gap-2">{getModuleIcon(invoice.module)} {invoice.module}</div>
                <div className="font-[350] text-sm text-gray-30 !w-[14%]">{invoice.device}</div>
                <div className={"font-[350] text-sm text-gray-30 text-left !w-[14%]"}> <span className={cn(invoice.outcome ? 'text-success-10' : '!text-error-30 bg-error-20 px-2 rounded-[8px] w-min')}>
                  {invoice.outcome ? "Success" : "Failed"}
                </span>
                </div>
                <div className="font-[350] text-sm text-gray-30 text-left !w-[14%]">{invoice.location}</div>
                <div className="font-[350] text-sm text-gray-30 text-left !w-[15%]">{invoice.timestamp}</div>
              </div>
            ))}
            </div>
          </div>
        </div>
        <footer className="">
          {isLoading ? null : <PaginationV3
            gotoPage={gotoPage}
            paginationData={paginationData(activityMeta?.totalPages)}
            gotoNext={() => {
              if (activityMeta?.totalPages === currentPage && !activityMeta?.hasNext) {
                return;
              }
              setCurrentPage((page) => page + 1);
            }}
            gotoPrev={() => {
              if (currentPage === 1 && !activityMeta?.hasPrevious) {
                return;
              }
              setCurrentPage((page) => page - 1);
            }}
            activePage={currentPage}
            hasPrevious={activityMeta?.hasPrevious}
            hasNext={activityMeta?.hasNext}
          />}
        </footer>
      </main>
    </section>
  )
}

export default ActivityLog
